import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import { api, type SipnavApiMapOperation } from '@/services/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  filterSipnavApiOperations,
  listSipnavApiFamilies,
  type SipnavApiMapAvailabilityFilter,
} from './sipnavApiMapState';

function availabilityLabel(operation: SipnavApiMapOperation): string {
  return operation.availability === 'available_in_telcoos'
    ? 'Available in TelcoOS'
    : 'Provider documented only';
}

function effectLabel(effect: string): string {
  const labels: Record<string, string> = {
    provider_read: 'Provider read',
    provider_write: 'Provider write',
    external_side_effect: 'External side effect',
    authentication: 'Authentication',
  };
  return labels[effect] ?? effect;
}

function verificationLabel(state: string): string {
  const labels: Record<string, string> = {
    verified: 'Live status matched the documentation',
    documented_response_differs: 'Live response differed from the documentation',
    documented_not_accessible: 'A valid signed resource was not available',
    documented: 'Documentation only',
    documentation_incomplete: 'Documentation is incomplete',
    not_yet_verified: 'Not verified',
  };
  return labels[state] ?? state;
}

function MethodBadge({ method }: { method: string }) {
  const tones: Record<string, string> = {
    GET: 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300',
    POST: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300',
    PUT: 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300',
    PATCH: 'bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300',
    DELETE: 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300',
  };
  return (
    <span className={cn(
      'inline-flex shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px] font-semibold',
      tones[method] ?? 'bg-surface-100 text-surface-700 dark:bg-surface-800 dark:text-surface-300'
    )}>
      {method}
    </span>
  );
}

function JsonShape({
  value,
  emptyMessage,
}: {
  value: unknown;
  emptyMessage: string;
}) {
  const isEmpty = value === null
    || value === undefined
    || (Array.isArray(value) && value.length === 0);
  if (isEmpty) {
    return (
      <p className="rounded-md border border-dashed border-surface-300 px-3 py-2 text-sm text-surface-500 dark:border-surface-700 dark:text-surface-400">
        {emptyMessage}
      </p>
    );
  }
  return (
    <pre className="max-h-80 overflow-auto rounded-md bg-surface-950 p-3 text-xs leading-relaxed text-surface-100">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function OperationDetail({
  operation,
  capabilities,
}: {
  operation: SipnavApiMapOperation;
  capabilities: Awaited<ReturnType<typeof api.connectors.getSipnavApiMap>>['capabilities'];
}) {
  const relatedCapabilities = capabilities.filter((capability) => (
    operation.capabilityIds.includes(capability.id)
  ));
  return (
    <article className="space-y-5 p-5">
      <div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <MethodBadge method={operation.method} />
          <code className="break-all text-sm font-semibold text-surface-900 dark:text-surface-100">
            {operation.path}
          </code>
        </div>
        <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100">
          {operation.summary || operation.operationId}
        </h3>
        {operation.description && (
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-surface-600 dark:text-surface-300">
            {operation.description}
          </p>
        )}
      </div>

      <dl className="grid gap-3 rounded-lg border border-surface-200 p-4 text-sm dark:border-surface-800 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-surface-500">Availability</dt>
          <dd className={cn(
            'mt-1 font-medium',
            operation.availability === 'available_in_telcoos'
              ? 'text-emerald-700 dark:text-emerald-300'
              : 'text-amber-700 dark:text-amber-300'
          )}>
            {availabilityLabel(operation)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-surface-500">Effect</dt>
          <dd className="mt-1 text-surface-800 dark:text-surface-200">
            {effectLabel(operation.documentedEffect)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-surface-500">Operation ID</dt>
          <dd className="mt-1 break-all font-mono text-xs text-surface-800 dark:text-surface-200">
            {operation.operationId}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-surface-500">Live verification</dt>
          <dd className="mt-1 text-surface-800 dark:text-surface-200">
            {verificationLabel(operation.verificationState)}
            {operation.verifiedOn ? ` · ${operation.verifiedOn}` : ''}
          </dd>
        </div>
      </dl>

      {relatedCapabilities.length > 0 ? (
        <section>
          <h4 className="mb-2 text-sm font-semibold text-surface-900 dark:text-surface-100">
            TelcoOS capabilities
          </h4>
          <div className="space-y-2">
            {relatedCapabilities.map((capability) => (
              <div key={capability.id} className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 dark:border-emerald-900 dark:bg-emerald-950/20">
                <code className="text-xs font-semibold text-emerald-800 dark:text-emerald-300">
                  {capability.id}
                </code>
                <p className="mt-1 text-sm font-medium text-surface-800 dark:text-surface-200">
                  {capability.title}
                </p>
                <p className="mt-1 text-xs text-surface-600 dark:text-surface-400">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </section>
      ) : (
        <section className="rounded-md border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300">
          TelcoOS does not have a reviewed capability for this operation.
        </section>
      )}

      <section>
        <h4 className="mb-2 text-sm font-semibold text-surface-900 dark:text-surface-100">
          Parameters
        </h4>
        <JsonShape value={operation.parameters} emptyMessage="No parameters are documented." />
      </section>

      <section>
        <h4 className="mb-2 text-sm font-semibold text-surface-900 dark:text-surface-100">
          Documented request shape
        </h4>
        <JsonShape value={operation.requestSchemas} emptyMessage="No request body schema is documented." />
      </section>

      <section>
        <h4 className="mb-2 text-sm font-semibold text-surface-900 dark:text-surface-100">
          Documented response shape
        </h4>
        <JsonShape value={operation.responseSchemas} emptyMessage="No response schema is documented." />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {operation.responses.map((response) => (
            <span
              key={response.status}
              title={response.description ?? undefined}
              className="rounded bg-surface-100 px-2 py-1 font-mono text-[11px] text-surface-600 dark:bg-surface-800 dark:text-surface-300"
            >
              {response.status}{response.description ? ` · ${response.description}` : ''}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h4 className="mb-2 text-sm font-semibold text-surface-900 dark:text-surface-100">
          Observed live response structure
        </h4>
        <JsonShape
          value={operation.observedResponseStructure}
          emptyMessage="No sanitized observed response structure is recorded."
        />
        {operation.documentationDifferences.length > 0 && (
          <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300">
            <p className="font-medium">Documentation differences</p>
            <JsonShape value={operation.documentationDifferences} emptyMessage="No differences are recorded." />
          </div>
        )}
      </section>

      {operation.documentationGaps.length > 0 && (
        <section>
          <h4 className="mb-2 text-sm font-semibold text-surface-900 dark:text-surface-100">
            Documentation gaps
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {operation.documentationGaps.map((gap) => (
              <span key={gap} className="rounded bg-amber-100 px-2 py-1 font-mono text-[11px] text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                {gap}
              </span>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

export function SipnavApiMapModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [catalog, setCatalog] = useState<Awaited<ReturnType<typeof api.connectors.getSipnavApiMap>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [availability, setAvailability] = useState<SipnavApiMapAvailabilityFilter>('all');
  const [family, setFamily] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || catalog) return;
    let active = true;
    setLoading(true);
    setError(null);
    void api.connectors.getSipnavApiMap()
      .then((result) => {
        if (active) setCatalog(result);
      })
      .catch((loadError) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'The SipNav API map could not be loaded.');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      setLoading(false);
    };
  }, [catalog, isOpen]);

  const families = useMemo(
    () => listSipnavApiFamilies(catalog?.operations ?? []),
    [catalog]
  );
  const filteredOperations = useMemo(() => filterSipnavApiOperations({
    operations: catalog?.operations ?? [],
    capabilities: catalog?.capabilities ?? [],
    query,
    availability,
    family,
  }), [availability, catalog, family, query]);
  const selectedOperation = filteredOperations.find(
    (operation) => operation.operationKey === selectedKey
  ) ?? filteredOperations[0] ?? null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/55" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sipnav-api-map-title"
        className="relative flex h-[94vh] w-full max-w-[96rem] flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-surface-900"
      >
        <header className="flex shrink-0 items-center justify-between border-b border-surface-200 px-4 py-3 dark:border-surface-800 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700 dark:bg-primary-950/60 dark:text-primary-300">
              <BookOpen className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h2 id="sipnav-api-map-title" className="truncate text-lg font-semibold text-surface-900 dark:text-surface-100">
                SipNav API Map
              </h2>
              <p className="truncate text-xs text-surface-500 dark:text-surface-400">
                Committed provider documentation and reviewed TelcoOS capability coverage
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close SipNav API Map">
            <X className="h-5 w-5" />
          </Button>
        </header>

        {loading && (
          <div className="flex flex-1 items-center justify-center gap-2 text-sm text-surface-600 dark:text-surface-300">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading the committed API map…
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-1 items-center justify-center p-6">
            <div className="max-w-md rounded-lg border border-red-200 bg-red-50 p-5 text-center text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              <AlertTriangle className="mx-auto mb-2 h-6 w-6" />
              <p className="font-medium">The API map is unavailable.</p>
              <p className="mt-1 text-sm">{error}</p>
            </div>
          </div>
        )}

        {!loading && catalog && (
          <>
            <div className="shrink-0 border-b border-surface-200 p-3 dark:border-surface-800 sm:px-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <label className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
                  <input
                    autoFocus
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search purpose, path, operation, fields, or capability…"
                    className="h-10 w-full rounded-md border border-surface-300 bg-white pl-9 pr-3 text-sm text-surface-900 outline-none ring-primary-500 focus:ring-2 dark:border-surface-700 dark:bg-surface-950 dark:text-surface-100"
                  />
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {([
                    ['all', `All ${catalog.counts.operations}`],
                    ['available_in_telcoos', `Available ${catalog.counts.availableInTelcoOS}`],
                    ['provider_documented_only', `Provider only ${catalog.counts.providerDocumentedOnly}`],
                  ] as const).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setAvailability(value)}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                        availability === value
                          ? 'border-primary-600 bg-primary-600 text-white'
                          : 'border-surface-300 text-surface-600 hover:bg-surface-100 dark:border-surface-700 dark:text-surface-300 dark:hover:bg-surface-800'
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-surface-500 dark:text-surface-400">
                <span>Snapshot {catalog.source.capturedOn}</span>
                <span>{catalog.counts.providerReads} provider reads</span>
                <span>{catalog.counts.capabilities} TelcoOS capabilities</span>
                <span>{filteredOperations.length} results</span>
              </div>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[13rem_20rem_minmax(0,1fr)]">
              <aside className="hidden overflow-y-auto border-r border-surface-200 p-2 dark:border-surface-800 md:block">
                <button
                  type="button"
                  onClick={() => setFamily(null)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm',
                    family === null
                      ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/40 dark:text-primary-300'
                      : 'text-surface-600 hover:bg-surface-100 dark:text-surface-300 dark:hover:bg-surface-800'
                  )}
                >
                  <span>All functions</span>
                  <span className="text-xs text-surface-400">{catalog.counts.operations}</span>
                </button>
                {families.map((entry) => (
                  <button
                    key={entry.name}
                    type="button"
                    onClick={() => setFamily(entry.name)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm',
                      family === entry.name
                        ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/40 dark:text-primary-300'
                        : 'text-surface-600 hover:bg-surface-100 dark:text-surface-300 dark:hover:bg-surface-800'
                    )}
                  >
                    <span>{entry.name}</span>
                    <span className="text-xs text-surface-400">{entry.count}</span>
                  </button>
                ))}
              </aside>

              <section className="min-h-0 overflow-y-auto border-r border-surface-200 dark:border-surface-800">
                {filteredOperations.length === 0 ? (
                  <div className="p-6 text-center text-sm text-surface-500 dark:text-surface-400">
                    No API operation matches this search.
                  </div>
                ) : filteredOperations.map((operation) => (
                  <button
                    key={operation.operationKey}
                    type="button"
                    onClick={() => setSelectedKey(operation.operationKey)}
                    className={cn(
                      'block w-full border-b border-surface-100 p-3 text-left transition-colors dark:border-surface-800',
                      selectedOperation?.operationKey === operation.operationKey
                        ? 'bg-primary-50/80 dark:bg-primary-950/30'
                        : 'hover:bg-surface-50 dark:hover:bg-surface-800/60'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <MethodBadge method={operation.method} />
                      <code className="min-w-0 truncate text-xs text-surface-700 dark:text-surface-300">
                        {operation.path}
                      </code>
                    </div>
                    <p className="mt-1.5 line-clamp-2 text-sm font-medium text-surface-900 dark:text-surface-100">
                      {operation.summary || operation.operationId}
                    </p>
                    <div className={cn(
                      'mt-2 flex items-center gap-1 text-[11px]',
                      operation.availability === 'available_in_telcoos'
                        ? 'text-emerald-700 dark:text-emerald-300'
                        : 'text-amber-700 dark:text-amber-300'
                    )}>
                      {operation.availability === 'available_in_telcoos'
                        ? <CheckCircle2 className="h-3 w-3" />
                        : <BookOpen className="h-3 w-3" />}
                      {availabilityLabel(operation)}
                    </div>
                  </button>
                ))}
              </section>

              <section className="min-h-0 overflow-y-auto bg-surface-50/40 dark:bg-surface-950/30">
                {selectedOperation ? (
                  <OperationDetail operation={selectedOperation} capabilities={catalog.capabilities} />
                ) : (
                  <div className="flex h-full items-center justify-center p-6 text-sm text-surface-500">
                    Select an API operation.
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
